/**
 * ReleaseNotesAnnouncer — polls /api/release-notes/unseen on mount and shows
 * a stack of dismissible modal cards for any release the current user
 * hasn't seen yet. Each card has a "Got it!" button that marks the release
 * as seen and moves to the next one.
 *
 * Mount this once near the auth boundary (e.g. inside the layout that wraps
 * authenticated routes). It silently does nothing for logged-out users.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { Sparkles, ChevronRight, Bell, CheckCircle2, Zap, Star } from "lucide-react";

const ICON_MAP = {
  Sparkles,
  Bell,
  CheckCircle2,
  Zap,
  Star,
};

export function ReleaseNotesAnnouncer() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!user) return;
    api
      .get("/release-notes/unseen")
      .then(({ data }) => {
        if (data && data.length > 0) {
          setItems(data);
          setIndex(0);
          setOpen(true);
        }
      })
      .catch(() => {
        /* silent */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!user || items.length === 0) return null;

  const current = items[index];
  if (!current) return null;
  const Icon = ICON_MAP[current.icon] || Sparkles;
  const hasMore = index < items.length - 1;

  async function dismissCurrent() {
    setDismissing(true);
    try {
      await api.post("/release-notes/seen", { id: current.id });
    } catch {
      /* don't block the UI on network error — just move on */
    } finally {
      setDismissing(false);
    }
    if (hasMore) {
      setIndex((i) => i + 1);
    } else {
      setOpen(false);
      setItems([]);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismissCurrent()}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-0"
        data-testid="release-notes-modal"
      >
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white px-6 py-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-200 font-semibold">
            <Sparkles size={13} /> Novidades · v{current.version}
            {items.length > 1 && (
              <span className="ml-auto bg-white/15 px-2 py-0.5 rounded-full text-[10px]">
                {index + 1}/{items.length}
              </span>
            )}
          </div>
          <DialogHeader className="mt-2">
            <DialogTitle className="text-white text-xl leading-tight flex items-start gap-2">
              <Icon size={22} className="flex-shrink-0 mt-0.5" />
              {current.title}
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 py-5">
          <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
            {current.body}
          </div>
        </div>

        <div className="px-6 pb-5 flex items-center justify-end gap-2">
          {hasMore ? (
            <Button
              data-testid="release-notes-next"
              onClick={dismissCurrent}
              disabled={dismissing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Próxima <ChevronRight size={14} className="ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="release-notes-got-it"
              onClick={dismissCurrent}
              disabled={dismissing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Entendi
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ReleaseNotesAnnouncer;
